import { Session } from "@insite/client-framework/Services/SessionService";
import { BillToModel, CartModel } from "@insite/client-framework/Types/ApiModels";
import { getZipCodeAddressCookie } from "@west/App/Handlers/ZipCode/ZipCodeHelper";
import { xGoogleAnalyticsHandlerType } from "@west/App/Store/Reducers";

// get global for google tag manager
export function getDataLayer() {
    if (!(window as any).dataLayer) {
        (window as any).dataLayer = [];
    }
    return (window as any).dataLayer;
}

interface CustomTransactionData {
    event: string;
    currency?: string;
    transactionId: string;
    transactionAffiliation?: string;
    transactionTotal: number;
    transactionTax: number;
    transactionShipping: number;
    transactionProducts: { sku: string; name: string; price?: number; quantity: number | null }[];
    firstName?: string;
    lastName?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    phoneNumber?: string;
    email?: string;
    address?: string;
    country?: string;
}

interface CustomAddToCartData {
    event: string;
    firstName?: string;
    lastName?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    phoneNumber?: string;
    email?: string;
    address?: string;
    country?: string;
}

export function customTrackCompletedOrder(cart: CartModel, billTo?: BillToModel, session?: Session) {
    if (IS_SERVER_SIDE) {
        return;
    }

    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const data: CustomTransactionData = {
        event: "transactionComplete",
        currency: session?.currency?.currencyCode,
        transactionId: cart.orderNumber,
        transactionAffiliation: billTo?.companyName,
        transactionTotal: cart.orderGrandTotal,
        transactionTax: cart.totalTax,
        transactionShipping: cart.shippingAndHandling,
        transactionProducts: [],
        firstName: billTo?.firstName,
        lastName: billTo?.lastName,
        city: billTo?.city,
        state: billTo?.state?.name,
        zipCode: billTo?.postalCode,
        phoneNumber: billTo?.phone,
        email: billTo?.email,
        address: billTo?.address1,
        country: billTo?.country?.name,
    };

    cart.cartLines?.forEach(cartLine => {
        data.transactionProducts.push({
            sku: cartLine.erpNumber,
            name: cartLine.shortDescription,
            price: cartLine.pricing?.unitNetPrice,
            quantity: cartLine.qtyOrdered,
        });
    });
    dataLayer.push(data);
}

export function customTrackAddToCartEvent(billTo?: BillToModel) {
    if (IS_SERVER_SIDE) {
        return;
    }

    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const cookie = getZipCodeAddressCookie();

    const data: CustomAddToCartData = {
        event: "AddToCart",
        firstName: billTo?.firstName,
        lastName: billTo?.lastName,
        city: billTo?.city,
        state: billTo?.state?.name,
        zipCode: billTo?.postalCode ?? cookie?.zipCode,
        phoneNumber: billTo?.phone,
        email: billTo?.email,
        address: billTo?.address1,
        country: billTo?.country?.name,
    };

    dataLayer.push(data);
}

export function customTrackNavigateToCheckoutEvent(billTo?: BillToModel) {
    if (IS_SERVER_SIDE) {
        return;
    }

    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const cookie = getZipCodeAddressCookie();

    const data: CustomAddToCartData = {
        event: "InitiateCheckout",
        firstName: billTo?.firstName,
        lastName: billTo?.lastName,
        city: billTo?.city,
        state: billTo?.state?.name,
        zipCode: billTo?.postalCode ?? cookie?.zipCode,
        phoneNumber: billTo?.phone,
        email: billTo?.email,
        address: billTo?.address1,
        country: billTo?.country?.name,
    };

    dataLayer.push(data);
}

/* eslint-disable */
(function (window) {
    (window as any)._GaCheckoutFunnel = (
        step: number,
        cart: CartModel,
        includeBrand?: boolean,
        includeCategory?: boolean,
        affiliation?: string,
        authenticationState?: string,
    ) => {
        const dataLayer = getDataLayer();
        if (!dataLayer) {
            return;
        }

        const googleAnalyticsData = {
            ...(step === 1 && {
                event: "begin_checkout",
            }),
            ...(step === 2 && {
                event: "add_shipping_info",
                shipping_tier: cart.carrier?.description || "",
            }),
            ...(step === 3 && {
                event: "add_payment_info",
                payment_type: cart.paymentMethod?.description || "",
            }),
            items: cart?.cartLines?.map(cartLine => ({
                item_name: cartLine.shortDescription,
                item_id: cartLine.erpNumber,
                price: cartLine.pricing?.unitNetPrice,
                quantity: cartLine.qtyOrdered,
                item_brand: includeBrand ? cartLine.brand?.name || "" : undefined,
                item_category: includeCategory ? cartLine.properties?.["category"] || "" : undefined,
                item_list_id: includeCategory ? cartLine.properties?.["categoryId"] || "" : undefined,
            })),
            value: cart.orderSubTotal,
            affiliation,
            authentication_state: authenticationState,
        };

        dataLayer.push(googleAnalyticsData);
    };
})(typeof window != "undefined" ? window : {});
/* eslint-enable */

export function googleAnalyticsFileDownload(
    xGoogleAnalytics: xGoogleAnalyticsHandlerType,
    clickText: string,
    destinationUrl: string,
) {
    if (xGoogleAnalytics.fileDownloadEvent && destinationUrl) {
        const dataLayer = getDataLayer();

        if (!dataLayer) {
            return;
        }

        const googleAnalyticsData = {
            event: xGoogleAnalytics.fileDownloadEventName,
            click_text: clickText,
            destination: destinationUrl,
            affiliation: xGoogleAnalytics.affiliation,
            authentication_state: xGoogleAnalytics.authenticationState,
        };

        dataLayer.push(googleAnalyticsData);
    }
}
export function googleAnalyticsSearchAutoCompleteTracking(
    xGoogleAnalytics: xGoogleAnalyticsHandlerType,
    label: string,
    destinationUrl: string,
) {
    if (xGoogleAnalytics.autoCompleteSearchEvent && destinationUrl) {
        const dataLayer = getDataLayer();

        if (!dataLayer) {
            return;
        }

        const googleAnalyticsData = {
            event: xGoogleAnalytics.autoCompleteSearchEventName,
            click_text: label,
            destination: destinationUrl,
            affiliation: xGoogleAnalytics.affiliation,
            authentication_state: xGoogleAnalytics.authenticationState,
        };

        dataLayer.push(googleAnalyticsData);
    }
}

export function googleAnalyticsSearchTracking(
    xGoogleAnalytics: xGoogleAnalyticsHandlerType,
    searchQuery: string,
    searchResultsCount: number,
    correctedQuery?: string,
) {
    if (xGoogleAnalytics.searchEvent) {
        const dataLayer = getDataLayer();

        if (!dataLayer) {
            return;
        }

        const googleAnalyticsData = {
            event: xGoogleAnalytics.searchEventName,
            search_query: searchQuery,
            num_search_results: searchResultsCount,
            corrected_query: correctedQuery || "",
            affiliation: xGoogleAnalytics.affiliation,
            authentication_state: xGoogleAnalytics.authenticationState,
        };

        dataLayer.push(googleAnalyticsData);
    }
}

export const googleAnalyticsNeedHelpClickHandler = (
    xGoogleAnalytics?: xGoogleAnalyticsHandlerType,
    url?: string,
    label?: string,
) => {
    if (xGoogleAnalytics && xGoogleAnalytics.needHelpClickEvent) {
        const dataLayer = getDataLayer();
        if (!dataLayer) {
            return;
        }
        const googleAnalyticsData = {
            event: xGoogleAnalytics.needHelpClickEventName,
            click_text: label,
            destination: url || "",
            affiliation: xGoogleAnalytics.affiliation,
            authentication_state: xGoogleAnalytics.authenticationState,
        };

        dataLayer.push(googleAnalyticsData);
    }
};
export const gaRichContentLinkClickHandler = (
    xGoogleAnalytics?: xGoogleAnalyticsHandlerType,
    url?: string,
    label?: string,
) => {
    if (xGoogleAnalytics) {
        const dataLayer = getDataLayer();
        if (!dataLayer) {
            return;
        }
        const googleAnalyticsData = {
            event: "link_click_event",
            click_text: label,
            click_class: xGoogleAnalytics.currentPage,
            destination: url || "",
            affiliation: xGoogleAnalytics.affiliation,
            authentication_state: xGoogleAnalytics.authenticationState,
        };

        dataLayer.push(googleAnalyticsData);
    }
};

export const myAccountFlyoutLinkClickHandler = (
    xGoogleAnalytics: xGoogleAnalyticsHandlerType,
    url: string,
    label?: string,
) => {
    if (xGoogleAnalytics.myAccountFlyoutEvent && url) {
        const dataLayer = getDataLayer();
        if (!dataLayer) {
            return;
        }

        const googleAnalyticsData = {
            event: xGoogleAnalytics.myAccountFlyoutEventName,
            click_text: label,
            destination: url,
            affiliation: xGoogleAnalytics.affiliation,
            authentication_state: xGoogleAnalytics.authenticationState,
        };

        dataLayer.push(googleAnalyticsData);
    }
};
